/**
 * MOBİL PANEL (BOTTOM SHEET) KONTROLCÜSÜ — Faz 2
 *
 * Eski mobil akış 3 sayfalık bir sihirbazdı: ürün seç -> özelleştir -> fiyat.
 * Her adımda 3D sahne yer değiştiriyor, `.active` / `.top` / `.top2` sınıfları
 * ve satır içi z-index değerleri elle didikleniyordu
 * (HawkUIController.initMobileUI, ~100 satır).
 *
 * Yeni model: 3D sahne HER ZAMAN tam ekran ve arkada durur. Alttaki sekme
 * çubuğundan açılan paneller onun üstüne kayar. Kullanıcı koltuğu gözden
 * kaybetmez — asıl mesele o.
 *
 * DOM'a dokunulmaz: mevcut elemanlar yerlerinde kalır, yalnızca sınıf alır.
 * Böylece devralınan modüllerin id/sınıf sorguları çalışmaya devam eder.
 */

interface PanelTanimi {
  id: string;
  etiket: string;
  secici: string;
  ikon: string;
}

/** Sekme çubuğundaki ikonlar — satır içi SVG, `currentColor` ile boyanır. */
const IKONLAR = {
  urunler:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
  tema:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18 4.5 4.5 0 0 1 0-9 4.5 4.5 0 0 0 0-9Z"/></svg>',
  nakis:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7V5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5V7"/><path d="M12 4v16"/><path d="M9 20h6"/></svg>',
};

const PANELLER: PanelTanimi[] = [
  { id: 'urunler', etiket: 'Ürünler', secici: '.other-selection-options', ikon: IKONLAR.urunler },
  { id: 'tema', etiket: 'Tema', secici: '.customize-selection-container', ikon: IKONLAR.tema },
  { id: 'nakis', etiket: 'Nakış', secici: '#selection-containerid', ikon: IKONLAR.nakis },
];

class PanelKontrolcusu {
  private acikPanel: string | null = null;
  private paneller = new Map<string, HTMLElement>();
  private dugmeler = new Map<string, HTMLButtonElement>();
  private cubuk!: HTMLElement;
  private fiyatDokumu: HTMLElement | null = null;
  private fiyatTetikleyicileri: HTMLElement[] = [];
  private fiyatDokumuAcik = false;

  kur(): void {
    // Panelleri bul ve işaretle
    for (const tanim of PANELLER) {
      const el = document.querySelector<HTMLElement>(tanim.secici);
      if (!el) {
        console.warn(`[Hawk v2] Panel bulunamadı: ${tanim.secici}`);
        continue;
      }
      el.classList.add('v2-sheet');
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'false');
      el.setAttribute('aria-label', tanim.etiket);
      this.tutamacEkle(el);
      this.paneller.set(tanim.id, el);
    }

    this.modelYuklemesiniGeciktir();
    this.nakisAlaniniTasi();
    this.fiyatDokumunuTasi();
    this.siparisDugmesiniTasi();
    this.orijinalDeseniniGizle();
    this.yanRayiKur();
    this.disaridanKapatmayiKur();
    this.cubuguKur();
    this.olaylariBagla();
  }

  /**
   * KATEGORİ DEĞİŞİMİNDE MODEL YÜKLEMESİNİ ERTELE
   *
   * Devralınan akış: kategori sekmesine basılır basılmaz
   * `HawkProductLogic.switchCategory()` yeni GLB'yi yüklüyor, ardından o
   * kategorinin varsayılan ürününe programatik olarak tıklayıp dokularını
   * uyguluyordu. Kullanıcı henüz hiçbir şey seçmeden model değişiyordu.
   *
   * Yeni akış:
   *   1. Kategoriye basılır  -> yalnızca ürün listesi değişir, sahne durur
   *   2. Ürün seçilir        -> panel aşağı iner
   *   3. Model yüklenir      -> yükleme göstergesi (loadModel kendi açıyor)
   *   4. Dokular uygulanır   -> kart tıklaması yeniden tetiklenir
   *
   * Not: bir kategorideki tüm ürünler aynı GLB'yi paylaşıyor (renk/desen
   * varyantları), model yalnızca kategoriden geliyor. Aksesuarlar istisna:
   * `handleItemClick` onlar için kendi modelini ayrıca yüklüyor, o akış
   * bozulmuyor.
   */
  private modelYuklemesiniGeciktir(): void {
    const urunlerPaneli = this.paneller.get('urunler');
    const hawk3D = window.hawk3D as { loadModel?: (url: string, cb?: () => void) => void } | undefined;
    const hawkProduct = window.hawkProduct as
      | { switchCategory?: (k: string, url: string, id: string) => void }
      | undefined;

    if (!urunlerPaneli || !hawk3D?.loadModel || !hawkProduct?.switchCategory) return;

    const gercekYukle = hawk3D.loadModel.bind(hawk3D);
    const orijinalDegistir = hawkProduct.switchCategory.bind(hawkProduct);

    let ertelenenModel: string | null = null;
    let kullaniciDegistirdi = false;

    // Yakalama evresi: bu dinleyici HawkProductLogic'inkinden ÖNCE çalışsın
    // ki switchCategory çağrıldığında bayrak yerinde olsun.
    for (const dugme of document.querySelectorAll('.other-selection-button')) {
      dugme.addEventListener('click', (e) => {
        if (e.isTrusted) kullaniciDegistirdi = true;
      }, true);
    }

    hawkProduct.switchCategory = (kategori, modelUrl, varsayilanId) => {
      // İlk açılış (handleInitialLoad) programatik: normal yükleme sürsün,
      // yoksa sahne boş kalırdı.
      if (!kullaniciDegistirdi) {
        orijinalDegistir(kategori, modelUrl, varsayilanId);
        return;
      }
      kullaniciDegistirdi = false;
      ertelenenModel = modelUrl;

      // Bu çağrı boyunca model yüklemesini yut: liste yenilensin, sahne dursun.
      hawk3D.loadModel = () => { /* ertelendi */ };
      try {
        orijinalDegistir(kategori, modelUrl, varsayilanId);
      } finally {
        hawk3D.loadModel = gercekYukle;
      }
    };

    urunlerPaneli.addEventListener('click', (e) => {
      if (!ertelenenModel) return;
      const kart = (e.target as HTMLElement).closest<HTMLElement>('.hawkchair');
      if (!kart) return;

      // Kartın kendi dinleyicisini durdur: dokuları model geldikten sonra
      // uygulayacağız, yoksa eski modele uygulanırdı.
      //
      // `isTrusted` kontrolü YOK: paylaşım linki akışı (HawkUtils.applyFromURL)
      // kartı programatik olarak tıklıyor. Orada da bekleyen modelin yüklenmesi
      // gerekiyor, yoksa doku eski modele uygulanırdı.
      e.stopImmediatePropagation();
      e.preventDefault();

      const url = ertelenenModel;
      ertelenenModel = null;

      // Paneli yalnızca kullanıcının kendi seçiminde kapat.
      if (e.isTrusted) this.kapat();

      gercekYukle(url, () => {            // loadModel yükleme göstergesini kendi açıp kapatıyor
        (window.hawkMaterials as { captureInitialState?: () => void } | undefined)?.captureInitialState?.();
        kart.click();                     // artık erteleme yok, normal akış
      });
    }, true);
  }

  /**
   * Nakış bölgesi seçildiğinde açılan yükleme / isim yazdırma arayüzü
   * `#dynamic-customization-area` içine basılıyor. Devralınan yapıda bu alan
   * Tema panelinin içindeydi: kullanıcı Nakış > Ön Sırt'a basıyor, seçenekler
   * kapalı duran başka bir panelin içinde oluşuyor ve hiç görünmüyordu.
   *
   * Elemanı Nakış paneline taşıyoruz. Güvenli: devralınan kod ona yalnızca
   * `getElementById` ile erişiyor (3 yer) ve ata bağımlı CSS seçicisi yok.
   */
  private nakisAlaniniTasi(): void {
    const nakisPaneli = this.paneller.get('nakis');
    const dinamikAlan = document.getElementById('dynamic-customization-area');
    if (!nakisPaneli || !dinamikAlan) return;
    nakisPaneli.appendChild(dinamikAlan);
    this.nakisDuzenlemesiniIzle(nakisPaneli, dinamikAlan);
    this.nakisFiyatlariniYaz();
  }

  /**
   * Bölge düğmelerinin yanına ek ücreti yazar: "Ön Sırt   +800 ₺".
   * Kullanıcı hangi bölgenin ne kadar tuttuğunu seçmeden önce görüyor;
   * eskiden ücret ancak seçim yapıldıktan sonra fiyat dökümünde çıkıyordu.
   *
   * Tutarlar `window.customfiyatlar` dizisinden okunuyor (HawkProductLogic
   * bunu /common/definedetails yanıtıyla dolduruyor). Burada hiçbir rakam
   * sabitlenmiyor — fiyat değişince arayüz de kendiliğinden değişir.
   */
  private nakisFiyatlariniYaz(): void {
    const BASLIKLAR: Record<string, string> = {
      'önLogoEkle': 'Ön Logo',
      'arkaLogoEkle': 'Arka Logo',
      'belLogoEkle': 'Bel Logo',
      'isimYazdirma': 'Nickname',
      'yastikLogoEkle': 'Üst Yastık Logo',
    };

    const dugmeler = Object.entries(BASLIKLAR)
      .map(([id, baslik]) => ({ el: document.getElementById(id), baslik }))
      .filter((k): k is { el: HTMLElement; baslik: string } => !!k.el);

    const dene = (): boolean => {
      const fiyatlar = window.customfiyatlar;
      if (!fiyatlar || fiyatlar.length === 0) return false;

      for (const { el, baslik } of dugmeler) {
        if (el.querySelector('.v2-nakis-fiyat')) continue;
        const kayit = fiyatlar.find((f) => f.Title === baslik);
        if (!kayit) continue;
        const tutar = parseFloat(String(kayit.Description).replace(/\./g, '').replace(',', '.').trim());
        if (!isFinite(tutar)) continue;

        const etiket = document.createElement('span');
        etiket.className = 'v2-nakis-fiyat';
        etiket.textContent = `+${tutar.toLocaleString('tr-TR')} ₺`;
        el.appendChild(etiket);
      }
      return dugmeler.every((k) => k.el.querySelector('.v2-nakis-fiyat'));
    };

    // Katalog asenkron geliyor; gelene kadar kısa aralıklarla bak (en çok ~12sn).
    if (dene()) return;
    let deneme = 0;
    const sayac = window.setInterval(() => {
      if (dene() || ++deneme > 40) window.clearInterval(sayac);
    }, 300);
  }

  /**
   * Nakış paneli iki aşamalı: önce bölge (Ön Sırt / İsim Yazdırma ...), sonra
   * o bölgenin düzenleyicisi. İkinci aşamada devralınan `setUIMode('active')`
   * bölge düğmelerine yalnızca `opacity: 0` veriyor, `display` sabit kalıyor:
   * 5 düğme 260px'lik görünmez bir boşluk olarak duruyor ve asıl içeriği
   * (dosya seç kutusu, VAZGEÇ/ONAYLA) alt sekme çubuğunun altına itiyordu.
   *
   * Burada paneli düzenleme kipine alıp o düğmeleri akıştan çıkarıyoruz.
   */
  private nakisDuzenlemesiniIzle(panel: HTMLElement, alan: HTMLElement): void {
    const guncelle = (): void => {
      // Ölçüt "alan boş mu" olamaz: bölge seçim aşamasında da
      // `initCustomizationArea()` alana "Sipariş Oluştur" düğmesini koyuyor.
      // Onay şeridi ise yalnızca düzenleyici açıkken üretiliyor.
      const duzenlemede = !!alan.querySelector('#siparis-onay');
      panel.classList.toggle('v2-nakis-duzenleme', duzenlemede);
    };

    new MutationObserver(guncelle).observe(alan, { childList: true });
    guncelle();
  }

  /**
   * Fiyat dökümü (koltuk / desen / isim / ön / bel / arka satırları ve her
   * birinin "x" kaldırma düğmesi) kalıcı olarak yer kaplamak yerine, sol
   * alttaki kırmızı fiyat çipine dokununca açılan bir kutuya alınıyor.
   * Tekrar dokununca, dışarı dokununca ya da Esc ile kapanır.
   *
   * Devralınan yapıda Ürünler panelinin içindeydi ve orada 149px ölü alan
   * oluşturuyordu.
   */
  private fiyatDokumunuTasi(): void {
    const serit = document.querySelector<HTMLElement>('.total-price-bar');
    const dokum = document.querySelector<HTMLElement>('.price-container');
    if (!serit || !dokum) return;

    dokum.classList.add('v2-price-pop');
    dokum.setAttribute('role', 'dialog');
    dokum.setAttribute('aria-label', 'Fiyat dökümü');
    serit.appendChild(dokum);

    // Fiyat çipinin tamamı (etiket + tutar) tetikleyici olsun
    const tetikleyiciler = [
      document.getElementById('total-price'),
      document.getElementById('toplamfiyatsayı'),
    ].filter((e): e is HTMLElement => !!e);

    for (const t of tetikleyiciler) {
      t.setAttribute('role', 'button');
      t.setAttribute('tabindex', '0');
      t.setAttribute('aria-expanded', 'false');
      t.style.cursor = 'pointer';

      t.addEventListener('click', (e) => {
        e.stopPropagation();
        this.fiyatDokumunuDegistir();
      });
      t.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.fiyatDokumunuDegistir();
        }
      });
    }

    this.fiyatTetikleyicileri = tetikleyiciler;
    this.fiyatDokumu = dokum;
    this.fiyatEtiketleriniKisalt(dokum);

    // Dışarı dokununca kapansın. Dökümün kendi içindeki "x" düğmeleri
    // çalışmaya devam etmeli, bu yüzden kutunun içi hariç tutuluyor.
    document.addEventListener('click', (e) => {
      if (!this.fiyatDokumuAcik) return;
      if (dokum.contains(e.target as Node)) return;
      this.fiyatDokumunuKapat();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.fiyatDokumunuKapat();
    });
  }

  /**
   * Fiyat kutusundaki etiketleri kısaltır: "Arka Sırt :" -> "Arka".
   * Kutu dar bir kart olduğu için uzun etiketler gereksiz genişletiyordu;
   * bağlam zaten kutunun kendisi.
   *
   * Id üzerinden eşleştiriliyor, metinden değil: Türkçe'de "İsim"in küçük
   * harfe çevrimi güvenilir değil ve `#trkoltukfiyati` çalışma anında
   * HawkProductLogic tarafından "Koltuk Fiyatı :" / "Yastık Fiyatı :"
   * olarak yeniden yazılıyor — o yüzden MutationObserver ile takipte.
   * Gözlemci kendi değişikliğini tetikliyor, bu yüzden "zaten kısa" kontrolü
   * şart: yoksa sonsuz döngüye girer.
   */
  private fiyatEtiketleriniKisalt(dokum: HTMLElement): void {
    const kisaltmalar: Record<string, string> = {
      trdesenfiyati: 'Desen',
      trisimfiyati: 'İsim',
      tronfiyati: 'Ön',
      trbelfiyati: 'Bel',
      trarkafiyati: 'Arka',
    };

    const uygula = () => {
      for (const [id, kisa] of Object.entries(kisaltmalar)) {
        const el = document.getElementById(id);
        if (el && el.textContent !== kisa) el.textContent = kisa;
      }

      // Bu etiket kategoriye göre değişiyor; ilk kelimesini koruyoruz.
      const koltuk = document.getElementById('trkoltukfiyati');
      if (koltuk) {
        const kisa = koltuk.textContent?.trim().startsWith('Yastık') ? 'Yastık' : 'Koltuk';
        if (koltuk.textContent !== kisa) koltuk.textContent = kisa;
      }
    };

    uygula();
    new MutationObserver(uygula).observe(dokum, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private fiyatDokumunuDegistir(): void {
    if (this.fiyatDokumuAcik) this.fiyatDokumunuKapat();
    else this.fiyatDokumunuAc();
  }

  private fiyatDokumunuAc(): void {
    if (!this.fiyatDokumu) return;
    this.fiyatDokumu.classList.add('is-open');
    for (const t of this.fiyatTetikleyicileri) t.setAttribute('aria-expanded', 'true');
    this.fiyatDokumuAcik = true;
  }

  private fiyatDokumunuKapat(): void {
    if (!this.fiyatDokumu || !this.fiyatDokumuAcik) return;
    this.fiyatDokumu.classList.remove('is-open');
    for (const t of this.fiyatTetikleyicileri) t.setAttribute('aria-expanded', 'false');
    this.fiyatDokumuAcik = false;
  }

  /**
   * "Orijinal" desenini gizler. Deseni kaldırmak zaten Sıfırla düğmesiyle
   * ya da başka bir ürün seçerek yapılıyor; listede ayrı bir seçenek olarak
   * durması gereksiz.
   *
   * Ürün değişince desenler yeniden basıldığı için MutationObserver ile
   * takip ediliyor. Eşleştirme metne göre: `data-pattern-id` ürüne göre
   * değişiyor (Future'da 9), sabit bir id'ye güvenilemez.
   */
  private orijinalDeseniniGizle(): void {
    const kap = document.getElementById('patterns-container');
    if (!kap) return;

    // Sınıf kullanılıyor, satır içi `display:none` değil: desen ızgarası
    // kuralı `.pattern-content { display: flex !important }` içerdiği için
    // satır içi stil eziliyordu.
    const gizle = () => {
      for (const desen of kap.querySelectorAll<HTMLElement>('.pattern-content')) {
        if (desen.textContent?.trim().toLowerCase() === 'orijinal') {
          desen.classList.add('v2-gizli');
        }
      }
    };

    gizle();
    new MutationObserver(gizle).observe(kap, { childList: true, subtree: true });
  }

  /**
   * "Sipariş Oluştur" düğmesi devralınan yapıda Ürünler panelinin içindeki
   * fiyat listesinin dibindeydi: kullanıcı paneli açıp aşağı kaydırmadan
   * göremiyordu. Kalıcı alt şeride, toplam fiyatın yanına alıyoruz.
   *
   * Düğmenin etkin/pasif durumu devralınan `toggleOrderButtons()` tarafından
   * satır içi `opacity`/`pointerEvents` ile yönetiliyor ve id üzerinden
   * eriştiği için taşınması bu mantığı bozmuyor. Tasarım tamamlanmadan
   * düğme sönük kalır — kasıtlı.
   */
  private siparisDugmesiniTasi(): void {
    const cubuk = document.querySelector('.total-price-bar');
    const dugme = document.getElementById('siparis-button2');
    if (cubuk && dugme) cubuk.appendChild(dugme);
  }

  /**
   * Sağ kenardaki yüzen düğmeleri tek bir dikey raya toplar:
   * AR ("Evinde Görüntüle") · Yardım · Karanlık Mod · Sipariş Destek.
   * Hepsi sadece ikon; metinler CSS'te gizleniyor, erişilebilirlik için
   * `aria-label` ve `data-tooltip` yerinde kalıyor.
   *
   * Önceden bu üçü ekranın altında birbirinin ve sipariş şeridinin üzerine
   * biniyordu.
   *
   * ÖNEMLİ: elemanlar birbirinin İÇİNE değil, KARDEŞ olarak konuluyor.
   * Karanlık mod (HawkUIController.applyDarkModeStyles) hem `hawkHelper`
   * hem `modelviewerAR` üzerine `filter: invert()` uyguluyor; biri
   * diğerinin içinde olsaydı ters çevirme iki kez uygulanır ve ikon
   * eski hâline dönerdi.
   */
  private yanRayiKur(): void {
    const yardimci = document.getElementById('hawkHelper');
    const destek = document.getElementById('hawkSupportBtn');
    const ar = document.getElementById('modelviewerAR');
    if (!yardimci) return;

    const ray = document.createElement('div');
    ray.className = 'v2-rail';
    yardimci.parentElement?.insertBefore(ray, yardimci);

    // Sıra: yardım + karanlık mod, destek, en altta AR.
    // AR en son: tasarım bittikten sonra bakılan bir adım, sık kullanılan
    // denetimlerin üstünü kapatmaması için sıranın sonunda.
    ray.appendChild(yardimci);
    if (destek) ray.appendChild(destek);
    if (ar) ray.appendChild(ar);

  }

  /**
   * Panelin üstüne sürüklenebilir tutamak ekler.
   * Başlık metni yok: hangi panelin açık olduğunu alt sekme çubuğu zaten
   * gösteriyor, tekrar etmek yer kaplıyordu. Ekran okuyucular için panelin
   * `aria-label` niteliği aynı bilgiyi taşıyor.
   */
  private tutamacEkle(panel: HTMLElement): void {
    const tutamac = document.createElement('div');
    tutamac.className = 'v2-sheet__handle';
    tutamac.innerHTML = '<span class="v2-sheet__grip" aria-hidden="true"></span>';
    panel.prepend(tutamac);

    // Aşağı kaydırarak kapatma.
    // Parmağı canlı takip etmiyoruz: panel `bottom` ile konumlanıyor ve her
    // karede onu değiştirmek düzeni yeniden hesaplatır (mobilde takılır).
    // Eşiği geçen bir aşağı hareket paneli kapatır — davranış yine tanıdık.
    let baslangicY = 0;

    tutamac.addEventListener('touchstart', (e) => {
      baslangicY = e.touches[0].clientY;
    }, { passive: true });

    tutamac.addEventListener('touchend', (e) => {
      const fark = e.changedTouches[0].clientY - baslangicY;
      if (fark > 60) this.kapat();
    });
  }

  /**
   * "Dışarı dokununca kapat" — eskiden tam ekran bir perde elemanı yapıyordu.
   * O perde 3D sahnenin önünü kapattığı için panel açıkken koltuk
   * döndürülemiyordu; oysa nakışı farklı açılardan görmek tam da panel
   * açıkken gerekiyor. Perde kaldırıldı, işlev buraya taşındı.
   *
   * Sürükleme ile dokunuş ayrılıyor: parmağını gezdirmek modeli döndürür,
   * yalnızca yerinde duran kısa bir dokunuş paneli kapatır. Aksi halde
   * koltuğu her çevirmeye çalıştığında panel kapanırdı.
   */
  /**
   * Nakış düzenleyicisi bir karar bekliyor mu?
   *
   * Bölgeye logo yüklendikten sonra VAZGEÇ / ONAYLA şeridi ekranda durur ve
   * kullanıcı ikisinden birini seçene kadar KAYBOLMAMALI: seçim yapılmadan
   * panel kapanırsa müşteri, eklediği logoyu onaylaması gerektiğini hiç
   * öğrenemiyor.
   *
   * Telefonda dosya seçici kapandıktan sonra sayfaya düşen serbest bir
   * dokunuş paneli kapatabiliyordu; bu kontrol onu engelliyor.
   */
  private nakisKararBekliyorMu(): boolean {
    const serit = document.getElementById('siparis-onay');
    return !!serit && getComputedStyle(serit).display !== 'none';
  }

  private disaridanKapatmayiKur(): void {
    // Panelin kendisi, sekme çubuğu ve yüzen denetimler kapatma alanı değil.
    const DOKUNULMAZ = '.v2-sheet, .v2-tabbar, .v2-rail, .total-price-bar, .v2-price-pop';
    const KAYMA_ESIGI = 10; // piksel
    const SURE_ESIGI = 500; // ms

    let izle = false;
    let baslangicX = 0;
    let baslangicY = 0;
    let baslangicAn = 0;

    document.addEventListener(
      'pointerdown',
      (olay) => {
        izle = false;
        if (!this.acikPanel) return;
        // Karar bekleme kontrolü burada DEĞİL: `kapat()` içinde, tek yerde.
        if ((olay.target as HTMLElement).closest(DOKUNULMAZ)) return;
        izle = true;
        baslangicX = olay.clientX;
        baslangicY = olay.clientY;
        baslangicAn = Date.now();
      },
      true,
    );

    document.addEventListener(
      'pointerup',
      (olay) => {
        if (!izle) return;
        izle = false;
        const kayma = Math.hypot(olay.clientX - baslangicX, olay.clientY - baslangicY);
        if (kayma <= KAYMA_ESIGI && Date.now() - baslangicAn <= SURE_ESIGI) this.kapat();
      },
      true,
    );
  }

  private cubuguKur(): void {
    this.cubuk = document.createElement('nav');
    this.cubuk.className = 'v2-tabbar';
    this.cubuk.setAttribute('aria-label', 'Tasarım araçları');

    for (const tanim of PANELLER) {
      if (!this.paneller.has(tanim.id)) continue;

      const dugme = document.createElement('button');
      dugme.type = 'button';
      dugme.className = 'v2-tabbar__btn';
      dugme.dataset.panel = tanim.id;
      dugme.setAttribute('aria-expanded', 'false');
      dugme.innerHTML = `${tanim.ikon}<span>${tanim.etiket}</span>`;
      dugme.addEventListener('click', () => this.degistir(tanim.id));

      this.cubuk.appendChild(dugme);
      this.dugmeler.set(tanim.id, dugme);
    }

    document.body.appendChild(this.cubuk);
  }

  private olaylariBagla(): void {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.acikPanel) this.kapat();
    });

    const urunlerPaneli = this.paneller.get('urunler');

    // Ürün seçilince paneli kapat: kullanıcı koltuğu görmek istiyor.
    // Kartlar katalog geldikten sonra oluşturulduğu için olay delegasyonu.
    //
    // `isTrusted` kontrolü şart: kategori değiştirilince HawkProductLogic
    // .switchCategory() o kategorinin varsayılan ürün kartına programatik
    // olarak `.click()` atıyor. O tıklama da buraya düşüyordu ve kullanıcı
    // Future'dan Fab'a geçer geçmez panel kapanıyordu.
    urunlerPaneli?.addEventListener('click', (e) => {
      if (!e.isTrusted) return;
      const kart = (e.target as HTMLElement).closest('.hawkchair');
      if (kart) window.setTimeout(() => this.kapat(), 180);
    });

    // Kategori değişiminde yükleniyor göstergesi YOK.
    // Model artık kategori değişiminde yüklenmiyor (bkz. modelYuklemesiniGeciktir);
    // seri değiştirmek yalnızca listeyi tazeliyor ve anında oluyor.
    // Model yüklenirken gösterilen asıl gösterge Hawk3DCore.loadModel içinde
    // (#midLoader) ve o ürün seçilince devreye giriyor.
  }

  private degistir(id: string): void {
    if (this.acikPanel === id) this.kapat();
    else this.ac(id);
  }

  ac(id: string): void {
    const panel = this.paneller.get(id);
    if (!panel) return;

    // Panel açılınca fiyat şeridi gizleniyor; açık kalan döküm kutusu
    // havada asılı kalmasın.
    this.fiyatDokumunuKapat();

    if (this.acikPanel && this.acikPanel !== id) {
      this.paneller.get(this.acikPanel)?.classList.remove('v2-sheet--open');
      this.dugmeler.get(this.acikPanel)?.classList.remove('is-active');
      this.dugmeler.get(this.acikPanel)?.setAttribute('aria-expanded', 'false');
    }

    panel.classList.add('v2-sheet--open');
    this.dugmeler.get(id)?.classList.add('is-active');
    this.dugmeler.get(id)?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('v2-sheet-open');
    this.acikPanel = id;
  }

  kapat(): void {
    if (!this.acikPanel) return;

    // NAKIŞ PANELİ KARAR BEKLERKEN KAPANMAZ.
    //
    // Logo yüklendikten sonra VAZGEÇ / ONAYLA şeridi ekranda kalmalı;
    // kullanıcı ikisinden birine basmadan panel kapanırsa, eklediği logoyu
    // onaylaması gerektiğini hiç fark etmiyor.
    //
    // Kontrol tek tek kapatma yollarına değil buraya konuldu: aşağı
    // sürükleme, sekmeye tekrar dokunma, Esc, dışarı dokunuş ve programatik
    // çağrıların hepsi buradan geçiyor, hepsi birden kapsanıyor.
    //
    // Yalnızca Nakış paneline özel: başka bir panel açıkken (ör. Ürünler)
    // kapanma normal çalışmalı. Farklı bir sekmeye geçiş de etkilenmiyor,
    // çünkü onu `ac()` kendisi hallediyor, `kapat()` üzerinden gitmiyor.
    if (this.acikPanel === 'nakis' && this.nakisKararBekliyorMu()) return;

    this.paneller.get(this.acikPanel)?.classList.remove('v2-sheet--open');
    this.dugmeler.get(this.acikPanel)?.classList.remove('is-active');
    this.dugmeler.get(this.acikPanel)?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('v2-sheet-open');
    this.acikPanel = null;
  }
}

/**
 * SAYFA BAŞLIĞI (H1) YÜKLEME EKRANINA
 *
 * Üst şeritte yer kaplamasın diye başlık oradan alınıp açılış ekranının
 * içine konuyor: kullanıcı onu yalnızca yüklenirken görüyor, sonra üst
 * bölge tamamen logoya kalıyor.
 *
 * KRİTİK: yükleyici işi bitince `display: none` oluyor. Başlık onun
 * çocuğu olarak kalırsa render edilmiş DOM'da hiç bulunmaz ve
 * indekslenmez — sayfanın tek H1'i kaybolur. Bu yüzden yükleyici
 * gizlenirken başlık gövdeye geri alınıp yalnızca GÖRSEL olarak
 * gizleniyor (`clip-path`); DOM'da ve erişilebilirlik ağacında duruyor.
 *
 * Yine de: görünür bir H1, görsel olarak gizlenmiş olandan daha ağır
 * basar. Bu, "üst bölge temiz olsun" isteğiyle SEO arasında bilinçli
 * bir orta yol.
 */
function basligiYukleyiciyeTasi(): void {
  const baslik = document.querySelector<HTMLElement>('.banner-title');
  if (!baslik) return;

  const gizle = (): void => {
    if (baslik.parentElement !== document.body) document.body.appendChild(baslik);
    baslik.classList.remove('v2-yukleyici-basligi');
    baslik.classList.add('v2-baslik-gizli');
  };

  const yukleyiciyiIzle = (yukleyici: HTMLElement): void => {
    // "Şu an gizli" ile "yükleme bitti" aynı şey DEĞİL: yükleyici
    // oluşturulduğunda da gizli duruyor, `show()` çağrılınca açılıyor.
    // Bu yüzden yalnızca GÖRÜNÜR OLDUKTAN SONRA gizlenirse başlık kaldırılıyor.
    let gorulmustu = false;
    const bak = (): void => {
      if (getComputedStyle(yukleyici).display !== 'none') { gorulmustu = true; return; }
      if (gorulmustu) gizle();
    };
    new MutationObserver(bak).observe(yukleyici, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    bak();

    // Emniyet: yükleyici hiç açılmazsa başlık `display:none` bir kabın
    // içinde kalır ve indekslenmez. 10sn sonra hâlâ görünmediyse dışarı al.
    window.setTimeout(() => { if (!gorulmustu) gizle(); }, 10000);
  };

  const yerlestir = (): boolean => {
    const yukleyici = document.getElementById('hawk-smart-loader');
    // Başlık artık içeriğin ortasında değil, yükleyicinin ALT ŞERİDİNDE:
    // açılış ekranı da header + footer iskeletini koruyor, slogan sekme
    // çubuğunun duracağı yerde duruyor.
    const icerik = yukleyici?.querySelector('.hawk-loader-footer');
    if (!yukleyici || !icerik) return false;

    icerik.appendChild(baslik);
    baslik.classList.add('v2-yukleyici-basligi');
    yukleyiciyiIzle(yukleyici);
    return true;
  };

  // Yükleyici HawkLoader tarafından çalışma anında oluşturuluyor;
  // kontrolcü daha erken çalışabilir.
  if (yerlestir()) return;
  let deneme = 0;
  const sayac = window.setInterval(() => {
    if (yerlestir() || ++deneme > 40) {
      window.clearInterval(sayac);
      if (deneme > 40) gizle();   // yükleyici hiç gelmediyse başlığı kaybetme
    }
  }, 150);
}

export function panelleriKur(): void {
  const kontrolcu = new PanelKontrolcusu();

  const baslat = () => {
    // Başlık taşıma HER GENİŞLİKTE: üst şerit hem mobilde hem masaüstünde
    // kaldırıldı, dolayısıyla H1'in yeni yeri her iki durumda da açılış
    // ekranı. Aşağıdaki mobil kapısının ÜSTÜNDE olması şart.
    basligiYukleyiciyeTasi();

    // SADECE MOBİLDE.
    //
    // Kontrolcü yalnızca sınıf eklemiyor, DOM'u da taşıyor: fiyat dökümü alt
    // şeride, dinamik alan Nakış paneline, AR/destek/yardımcı düğmeler sağ
    // raya, sipariş düğmesi fiyat şeridine. CSS'in tamamı mobil media
    // query'sinde olduğu için masaüstünde görünüm kuralları uygulanmıyordu,
    // ama taşınan elemanlar orada da taşınmış kalıyor ve masaüstü düzenini
    // dağıtıyordu.
    //
    // Masaüstü sürümü Faz 2'nin ikinci yarısında ayrıca ele alınacak; o zamana
    // kadar orada eski düzen olduğu gibi çalışır.
    //
    // Kırılma noktası geçilirse (ör. tarayıcı penceresini daraltmak) yeniden
    // yükleme gerekir: taşımaları geri almak yarım durumlar üretirdi.
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    kontrolcu.kur();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', baslat, { once: true });
  } else {
    baslat();
  }
}
